import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllLeavesHistory } from '../../../Redux/Slice/LeaveForm';
import { fetchAllMembers } from '../../../Redux/Slice/AddMember';
import {
    FiUsers, FiCheckCircle, FiXCircle, FiActivity,
    FiPieChart, FiBarChart2, FiClock, FiDownload,
    FiSearch, FiFilter, FiCalendar, FiArrowUpRight,
    FiChevronDown, FiChevronUp, FiPhoneCall, FiTrendingUp, FiRepeat,
    FiTrendingDown
} from "react-icons/fi";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, BarChart, Bar, Cell, PieChart, Pie, Legend, Sector, LabelList
} from 'recharts';
import { format, parse } from 'date-fns';
import LeaveRecordTable from './LeaveRecordTable';
import Loader from '../../../Components/Loader/Loader';
import socket from '../../../socket';

const EmployeeHistory = () => {
    const dispatch = useDispatch();

    const { leaveData, listLoading, error } = useSelector((state) => state.LeaveForm);
    const { MemberData } = useSelector((state) => state.AddMemberSlice);

    const [expandedRows, setExpandedRows] = useState(null);
    const [activePieIndex, setActivePieIndex] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        employeeId: '',
        employeeName: '',
        department: 'All Departments',
        team: 'All Teams',
        leaveType: 'All Types',
        status: 'All Status',
        fromDate: '',
        toDate: ''
    });

    useEffect(() => {
        dispatch(fetchAllLeavesHistory());
        dispatch(fetchAllMembers());
    }, [dispatch]);

    useEffect(() => {

        socket.on("leaveUpdate", (data) => {
            console.log("Live leave update received (History):", data);
            dispatch(fetchAllLeavesHistory());
        });

        return () => {
            socket.off("leaveUpdate");
        };
    }, [dispatch]);


    // Mock/Derived Data Calculations
    const processedLeaves = leaveData || [];
    const totalRequests = processedLeaves.length;
    const approvedCount = processedLeaves.filter(l => l.Status === 'Approved').length;
    const rejectedCount = processedLeaves.filter(l => l.Status === 'Rejected').length;
    const withdrawnCount = processedLeaves.filter(l => l.Status === 'Withdrawn').length;
    const emergencyCount = processedLeaves.filter(l => l.LeaveType === 'Emergency Leave').length;

    const approvalRate = totalRequests > 0 ? ((approvedCount / totalRequests) * 100).toFixed(1) : 0;

    const calculateItemDeduction = (item) => {
        if (item.Status === 'Withdrawn' || item.Status === 'Rejected') return 0;

        const salary = parseFloat(item.applicant?.Salary) || 0;
        const dailyRate = salary / 26;
        const hourlyRate = dailyRate / 8.5;

        if (item.LeaveType === 'Half Day Leave') {
            return dailyRate / 2;
        }

        if (['Late Arrival', 'Time Break', 'Early Leave'].includes(item.LeaveType)) {
            // Calculate hours if times are available
            if (item.leavingTime && item.arrivalTime) {
                const parseTime = (timeStr) => {
                    const referenceDate = new Date('2000-01-01');
                    let parsed = parse(timeStr, 'HH:mm', referenceDate);
                    if (isNaN(parsed)) parsed = parse(timeStr, 'hh:mm a', referenceDate);
                    if (isNaN(parsed)) parsed = parse(timeStr, 'h:mm a', referenceDate);
                    if (isNaN(parsed)) parsed = parse(timeStr, 'HH:mm:ss', referenceDate);
                    return isNaN(parsed) ? null : parsed;
                };

                const start = parseTime(item.leavingTime);
                const end = parseTime(item.arrivalTime);
                if (start && end) {
                    const diffHours = (end - start) / (1000 * 60 * 60);
                    return diffHours > 0 ? diffHours * hourlyRate : 0;
                }
                return hourlyRate;
            }
        }

        // Default for full day types: Advance, Emergency, etc.
        const days = parseFloat(item.TotalDays) || 1;
        return dailyRate * days;
    };


    // Chart Data Preparation - Dynamic Department Data
    const depts = [...new Set(processedLeaves.map(l => l.applicantRole?.replace(/\b(Junior|Senior)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim() || 'Other'))];
    const departmentLeaveData = depts.map(dept => {
        const deptLeaves = processedLeaves.filter(l => (l.applicantRole?.replace(/\b(Junior|Senior)\b/gi, '')
            .replace(/\s+/g, ' ')
            .trim() || 'Other') === dept);
        const approved = deptLeaves.filter(l => l.Status === 'Approved').length;
        const pending = deptLeaves.filter(l => l.Status === 'Pending').length;
        const withdrawn = deptLeaves.filter(l => l.Status === 'Withdrawn').length;
        const rejected = deptLeaves.filter(l => l.Status === 'Rejected').length;

        return {
            name: dept,
            Approved: approved,
            Pending: pending,
            Withdrawn: withdrawn,
            Rejected: rejected,
            total: approved + pending + withdrawn + rejected
        };
    }).sort((a, b) => b.total - a.total).slice(0, 5);

    const leaveTypeDistribution = [
        { name: 'Late Arrival', value: processedLeaves.filter(l => l.LeaveType === 'Late Arrival').length || 0, color: '#10b981' },
        { name: 'Emergency Leave', value: processedLeaves.filter(l => l.LeaveType === 'Emergency Leave').length || 0, color: '#3b82f6' },
        { name: 'Time Break', value: processedLeaves.filter(l => l.LeaveType === 'Time Break').length || 0, color: '#06b6d4' },
        { name: 'Early Leave', value: processedLeaves.filter(l => l.LeaveType === 'Early Leave').length || 0, color: '#8b5cf6' },
        { name: 'Half Day Leave', value: processedLeaves.filter(l => l.LeaveType === 'Half Day Leave').length || 0, color: '#f43f5e' },
        { name: 'Advance Leave', value: processedLeaves.filter(l => l.LeaveType === 'Advance Leave').length || 0, color: '#f59e0b' },
    ];

    // Dynamic Team Calculation based on Member Roles
    const members = MemberData?.data || (Array.isArray(MemberData) ? MemberData : []);
    const uniqueRoles = [...new Set(members.map(m => m.Role).filter(Boolean))]
        .filter(role => !role.toLowerCase().includes('owner') && !role.toLowerCase().includes('senior developer') && !role.toLowerCase().includes('senior customer support'));

    const topTeamsData = uniqueRoles.map(role => {
        const count = processedLeaves.filter(l => l.applicantRole === role).length;
        let displayName = role;
        if (role === 'Web Junior Developer') displayName = 'Web Developer Team';
        if (role === 'Delphi Junior Developer') displayName = 'Delphi Developer Team';
        if (role === 'Junior Customer Support') displayName = 'Customer Support Team';
        if (role === 'Data Analyst') displayName = 'Data Analyst Team';

        return { name: displayName, count };
    }).sort((a, b) => b.count - a.count).slice(0, 5);

    const toggleRow = (id) => {
        setExpandedRows(prev => prev === id ? null : id);
    };

    const renderActiveShape = (props) => {
        const RADIAN = Math.PI / 180;
        const {
            cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle,
            fill, payload, percent, value,
        } = props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
        const ey = my;
        const textAnchor = cos >= 0 ? 'start' : 'end';

        return (
            <g>
                <text x={cx} y={cy} dy={8} textAnchor="middle" fill="#1e293b" className="font-bold text-xs uppercase tracking-tight">
                    {payload.name}
                </text>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius + 8}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <Sector
                    cx={cx}
                    cy={cy}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    innerRadius={outerRadius + 10}
                    outerRadius={outerRadius + 12}
                    fill={fill}
                />
                <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333" className='font-bold text-[11px]'>{`${value} Leaves`}</text>
                <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999" className='font-mono text-[10px]'>
                    {`(${(percent * 100).toFixed(2)}%)`}
                </text>
            </g>
        );
    };

    const CustomBarTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((acc, entry) => acc + (entry.value || 0), 0);
            return (
                <div className="bg-white px-5 py-4 rounded-2xl shadow-xl border border-gray-100 flex flex-col gap-2.5 min-w-[180px]">
                    <p className="text-xs font-semibold text-blue-900 uppercase tracking-[0.15em] mb-1 border-b border-gray-50 pb-2">
                        {label}
                    </p>
                    <div className="flex flex-col gap-2">
                        {payload.map((entry, index) => (
                            <div key={index} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                    <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wider">{entry.name}</span>
                                </div>
                                <span className="text-[#133A69] font-black text-xs">{entry.value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-50 flex justify-between items-center">
                        <span className="text-blue-600 text-[10px] font-black uppercase tracking-[0.1em]">Total Leaves</span>
                        <span className="text-blue-600 font-black text-xs">{total}</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const clearFilters = () => {
        setFilters({
            employeeId: '',
            employeeName: '',
            department: 'All Departments',
            team: 'All Teams',
            leaveType: 'All Types',
            status: 'All Status',
            fromDate: '',
            toDate: ''
        });
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Approved': return 'bg-green-50 text-green-600 border-green-100';
            case 'Rejected': return 'bg-red-50 text-red-600 border-red-100';
            case 'Withdrawn': return 'bg-gray-50 text-gray-500 border-gray-100';
            default: return 'bg-amber-50 text-amber-600 border-amber-100';
        }
    };

    const filteredRecords = processedLeaves.filter(item => {
        const matchesName = item.applicantName?.toLowerCase().includes(filters.employeeName.toLowerCase());
        const matchesId = item._id?.toLowerCase().includes(filters.employeeId.toLowerCase());
        const matchesType = filters.leaveType === 'All Types' || item.LeaveType === filters.leaveType;
        const matchesStatus = filters.status === 'All Status' || item.Status === filters.status;
        const matchesDepartment = filters.department === 'All Departments' || item.applicantRole === filters.department;

        // Date filtering
        let itemDateStr = item.createdAt;
        let matchesFromDate = true;
        let matchesToDate = true;
        if (itemDateStr) {
            const itemDate = new Date(itemDateStr);
            if (!isNaN(itemDate)) {
                if (filters.fromDate) {
                    const fromDate = new Date(`${filters.fromDate}T00:00:00`);
                    matchesFromDate = itemDate >= fromDate;
                }
                if (filters.toDate) {
                    const toDate = new Date(`${filters.toDate}T23:59:59`);
                    matchesToDate = itemDate <= toDate;
                }
            }
        } else {
            if (filters.fromDate || filters.toDate) {
                matchesFromDate = false;
                matchesToDate = false;
            }
        }

        return matchesName && matchesId && matchesType && matchesStatus && matchesDepartment && matchesFromDate && matchesToDate;
    });

    // Total Deduction Calculation based on filtered records
    const totalDeduction = filteredRecords.reduce((acc, l) => {
        return acc + calculateItemDeduction(l);
    }, 0);

    // Dynamic Average Approval Time Calculation
    const finalizedLeaves = processedLeaves.filter(l => l.Status === 'Approved' || l.Status === 'Pending');
    const totalApprovalTimeHrs = finalizedLeaves.reduce((acc, l) => {
        const start = new Date(l.createdAt);
        const end = new Date(l.updatedAt || l.createdAt);
        if (!isNaN(start) && !isNaN(end)) {
            const diffHours = (end - start) / (1000 * 60 * 60);
            return acc + (diffHours > 0 ? Math.floor(diffHours) : 0);
        }
        return acc;
    }, 0);
    const avgApprovalTimeValue = finalizedLeaves.length > 0 ? (totalApprovalTimeHrs / finalizedLeaves.length) : 0;
    const avgApprovalDisplay = avgApprovalTimeValue > 24
        ? (avgApprovalTimeValue / 24).toFixed(1) + 'd'
        : avgApprovalTimeValue.toFixed(1) + 'h';


    const handleExport = () => {
        if (!filteredRecords || filteredRecords.length === 0) {
            alert("No employee history records to export!");
            return;
        }

        const headers = ['Employee ID', 'Name', 'Department', 'Leave Type', 'Duration', 'Salary (PM)', 'Deduction', 'Status', 'Applied Date'];
        const csvData = filteredRecords.map(item => {
            const salary = parseFloat(item.applicant?.Salary) || 0;
            const deduction = calculateItemDeduction(item);
            return [
                item._id?.slice(-8).toUpperCase() || '-',
                item.applicantName || '-',
                item.applicantRole || '-',
                item.LeaveType || '-',
                item.TotalDays || (item.LeaveType === 'Time Break' ? `${item.leavingTime}-${item.arrivalTime}` : '1'),
                salary.toFixed(2),
                deduction.toFixed(2),
                item.Status || '-',
                item.createdAt ? format(new Date(item.createdAt), 'yyyy-MM-dd') : '-'
            ];
        });

        const csvContent = [
            headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(","),
            ...csvData.map(row => row.map(cell => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
        ].join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Employee_History_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (listLoading) {
        return <Loader />;
    }

    return (
        <>
            <div className="px-3 py-6 min-h-0 bg-[#f8fafc]/50">
                <div className="w-full space-y-6">
                    {/* Page Title */}
                    <div className="title mb-4">
                        <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                            Employee Performance & Leave Analytics
                        </h3>
                    </div>

                    {/* Top Action Row */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 bg-white p-5 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-gray-100 mb-6">
                        <div>
                            <h2 className="text-base font-bold text-gray-900 tracking-tight">Full Audit Dashboard</h2>
                            <p className="text-xs text-gray-600 font-semibold uppercase tracking-[0.15em] mt-1">Enterprise History & Trend Intelligence</p>
                        </div>
                        <button
                            onClick={handleExport}
                            className="w-full sm:w-auto flex items-center justify-center gap-3 px-6 py-2.5 bg-[#133A69] text-white rounded-xl text-xs font-bold shadow-xl shadow-blue-900/20 hover:bg-[#0c2545] transition-all active:scale-95 group"
                        >
                            <FiDownload className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                            EXPORT AUDIT DATA
                        </button>
                    </div>

                    {/* Stats Cards Grid - Premium Palette */}
                    <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Total Requests */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">Total Requests</p>
                                <h2 className="text-2xl font-bold text-gray-900 leading-none">{totalRequests}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-blue-50 text-[#133A69] flex items-center justify-center group-hover:scale-105 group-hover:bg-blue-100 transition-all border border-blue-100">
                                <FiBarChart2 size={24} />
                            </div>
                        </div>

                        {/* Approved */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">Approved</p>
                                <h2 className="text-2xl font-bold text-emerald-600 leading-none">{approvedCount}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center group-hover:scale-105 group-hover:bg-emerald-100 transition-all border border-emerald-100">
                                <FiCheckCircle size={24} />
                            </div>
                        </div>

                        {/* Rejected */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">Rejected</p>
                                <h2 className="text-2xl font-bold text-rose-600 leading-none">{rejectedCount}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center group-hover:scale-105 group-hover:bg-rose-100 transition-all border border-rose-100">
                                <FiXCircle size={24} />
                            </div>
                        </div>

                        {/* Approval Rate */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">Approval Rate</p>
                                <h2 className="text-2xl font-bold text-indigo-600 leading-none">{approvalRate}%</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center group-hover:scale-105 group-hover:bg-indigo-100 transition-all border border-indigo-100">
                                <FiTrendingUp size={24} />
                            </div>
                        </div>

                        {/* Avg Approval Time */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">Avg Resp. Time</p>
                                <h2 className="text-2xl font-bold text-purple-600 leading-none">{avgApprovalDisplay}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-500 flex items-center justify-center group-hover:scale-105 group-hover:bg-purple-100 transition-all border border-purple-100">
                                <FiClock size={24} />
                            </div>
                        </div>

                        {/* Emergency */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">High Priority</p>
                                <h2 className="text-2xl font-bold text-orange-600 leading-none">{emergencyCount}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center group-hover:scale-105 group-hover:bg-orange-100 transition-all border border-orange-100">
                                <FiActivity size={24} />
                            </div>
                        </div>

                        {/* Total Deduction - Refined Style */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-rose-100 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-rose-700 uppercase tracking-widest mb-1">Loss from Leave</p>
                                <h2 className="text-xl font-bold text-rose-600 leading-none">₹{totalDeduction?.toFixed(2)?.toLocaleString()}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center group-hover:scale-105 group-hover:bg-rose-100 transition-all border border-rose-100">
                                <FiTrendingDown size={24} />
                            </div>
                        </div>

                        {/* Total Withdrawn */}
                        <div className="bg-white p-5 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.02)] border border-gray-150 flex justify-between items-center group hover:translate-y-[-2px] transition-all duration-300">
                            <div>
                                <p className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-1">Withdrawn</p>
                                <h2 className="text-2xl font-bold text-gray-900 leading-none">{withdrawnCount}</h2>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-gray-50 text-gray-400 flex items-center justify-center group-hover:scale-105 group-hover:bg-gray-100 transition-all border border-gray-200">
                                <FiRepeat size={24} />
                            </div>
                        </div>
                    </div>
                </div>                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                    {/* Department-wise Bar Chart */}
                    <div className="bg-white p-6 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.02)] border border-gray-100 transition-all hover:shadow-[0_25px_60px_rgba(0,0,0,0.04)]">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-base font-bold text-gray-800 tracking-tight">Department-wise Breakdown</h3>
                            <div className="px-3 py-1 bg-blue-50 text-[#133A69] rounded-lg text-[10px] font-semibold uppercase">Live Stats</div>
                        </div>
                        <div className="h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={departmentLeaveData}
                                    margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                                    barSize={window.innerWidth < 640 ? 30 : 45}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: '700' }}
                                        dy={10}
                                    />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                                    <Tooltip content={<CustomBarTooltip />} cursor={{ fill: '#f8fafc', radius: 10 }} />
                                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '35px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }} />

                                    <Bar dataKey="Approved" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]}>
                                        <LabelList dataKey="Approved" position="middle" fill="#fff" fontSize={10} fontWeight="900" formatter={(val) => val > 0 ? val : ''} />
                                    </Bar>
                                    <Bar dataKey="Pending" stackId="a" fill="#f59e0b" radius={[0, 0, 0, 0]}>
                                        <LabelList dataKey="Pending" position="middle" fill="#fff" fontSize={10} fontWeight="900" formatter={(val) => val > 0 ? val : ''} />
                                    </Bar>
                                    <Bar dataKey="Withdrawn" stackId="a" fill="#94a3b8" radius={[0, 0, 0, 0]}>
                                        <LabelList dataKey="Withdrawn" position="middle" fill="#fff" fontSize={10} fontWeight="900" formatter={(val) => val > 0 ? val : ''} />
                                    </Bar>
                                    <Bar dataKey="Rejected" stackId="a" fill="#ef4444" radius={[12, 12, 0, 0]}>
                                        <LabelList dataKey="total" position="top" fill="#1e293b" fontSize={12} fontWeight="900" dy={-10} offset={10} />
                                        <LabelList dataKey="Rejected" position="middle" fill="#fff" fontSize={10} fontWeight="900" formatter={(val) => val > 0 ? val : ''} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Leave Type Pie Chart */}
                    <div className="bg-white p-6 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.02)] border border-gray-100 transition-all hover:shadow-[0_25px_60px_rgba(0,0,0,0.04)]">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-base font-bold text-gray-800 tracking-tight">Leave Type Distribution</h3>
                            <FiPieChart className="text-slate-500" size={18} />
                        </div>
                        <div className="h-[350px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        activeIndex={activePieIndex}
                                        activeShape={renderActiveShape}
                                        data={leaveTypeDistribution}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={75}
                                        outerRadius={105}
                                        paddingAngle={8}
                                        dataKey="value"
                                        stroke="none"
                                        onMouseEnter={(_, index) => setActivePieIndex(index)}
                                    >
                                        {leaveTypeDistribution.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                                style={{ filter: activePieIndex === index ? `drop-shadow(0 8px 15px ${entry.color}66)` : 'none' }}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip trigger="none" />
                                    <Legend
                                        layout="horizontal"
                                        verticalAlign="bottom"
                                        align="center"
                                        iconType="circle"
                                        formatter={(value) => (
                                            <span className="text-[11px] font-bold text-gray-600 ml-1 uppercase tracking-wider">{value}</span>
                                        )}
                                        wrapperStyle={{ paddingTop: '30px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Top Teams Horizontal Chart */}
                <div className="bg-white p-6 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.02)] border border-gray-100 mb-10 transition-all hover:shadow-[0_25px_60px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center justify-between mb-10">
                        <h3 className="text-base font-bold text-gray-800 tracking-tight">Leaderboard: Top Active Teams</h3>
                        <div className="p-2 bg-gray-50 rounded-xl">
                            <FiTrendingUp className="text-blue-600" />
                        </div>
                    </div>
                    <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={topTeamsData}
                                layout="vertical"
                                margin={{ left: 20, right: 60, top: 0, bottom: 0 }}
                                barSize={36}
                            >
                                <XAxis type="number" hide />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    width={window.innerWidth < 640 ? 100 : 150}
                                    tick={{ fontSize: 11, fill: '#64748b', fontWeight: '700' }}
                                />
                                <Tooltip cursor={{ fill: '#f8fafc', radius: 10 }} content={<CustomBarTooltip />} />
                                <Bar
                                    dataKey="count"
                                    fill="#133A69"
                                    radius={[0, 12, 12, 0]}
                                    background={{ fill: '#f8fafc', radius: [0, 12, 12, 0] }}
                                >
                                    <LabelList
                                        dataKey="count"
                                        position="insideRight"
                                        fill="#fff"
                                        fontSize={11}
                                        fontWeight="900"
                                        dx={-10}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                {/* Refined Control Center (Filters) - Light Theme */}
                <div className="bg-white p-6 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-gray-150 mb-10 relative overflow-hidden group">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gray-50/50 rounded-full -mr-32 -mt-32 transition-transform group-hover:scale-125 duration-1000"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-50/30 rounded-full -ml-24 -mb-24 transition-transform group-hover:scale-125 duration-1000"></div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-xl bg-[#133A69]/5 flex items-center justify-center border border-[#133A69]/10">
                                <FiFilter className="text-[#133A69] w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900 tracking-tight">Control Center</h3>
                                <p className="text-xs text-gray-650 font-semibold uppercase tracking-widest">Filter & Refine Data</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Employee ID */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">Global ID Search</label>
                                <div className="relative group/input">
                                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within/input:text-[#133A69] transition-colors" />
                                    <input
                                        type="text"
                                        value={filters.employeeId}
                                        onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                                        placeholder="EMP-XXXX"
                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:border-blue-500 focus:shadow-xl focus:shadow-blue-500/5 transition-all"
                                    />
                                </div>
                            </div>

                            {/* Employee Name */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">Employee Name</label>
                                <input
                                    type="text"
                                    value={filters.employeeName}
                                    onChange={(e) => handleFilterChange('employeeName', e.target.value)}
                                    placeholder="Enter full name..."
                                    className="w-full px-5 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:border-blue-500 transition-all"
                                />
                            </div>

                            {/* Department */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">Functional Unit</label>
                                <div className="relative group/select">
                                    <select
                                        value={filters.department}
                                        onChange={(e) => handleFilterChange('department', e.target.value)}
                                        className="w-full appearance-none px-5 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 outline-none focus:bg-white focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        <option value="All Departments">All Departments</option>
                                        {uniqueRoles?.map(role => (
                                            <option key={role} value={role}>{role}</option>
                                        ))}
                                    </select>
                                    <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-hover/select:text-[#133A69] pointer-events-none transition-colors" />
                                </div>
                            </div>

                            {/* Leave Type */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">Request Category</label>
                                <div className="relative group/select">
                                    <select
                                        value={filters.leaveType}
                                        onChange={(e) => handleFilterChange('leaveType', e.target.value)}
                                        className="w-full appearance-none px-5 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 outline-none focus:bg-white focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        <option value="All Types">All Types</option>
                                        <option value="Advance Leave">Advance Leave</option>
                                        <option value="Half Day Leave">Half Day Leave</option>
                                        <option value="Early Leave">Early Leave</option>
                                        <option value="Late Arrival">Late Arrival</option>
                                        <option value="Time Break">Time Break</option>
                                        <option value="Emergency Leave">Emergency Leave</option>
                                    </select>
                                    <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-hover/select:text-[#133A69] pointer-events-none transition-colors" />
                                </div>
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">Processing Status</label>
                                <div className="relative group/select">
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full appearance-none px-5 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 outline-none focus:bg-white focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        <option value="All Status">All Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                        <option value="Withdrawn">Withdrawn</option>
                                    </select>
                                    <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-hover/select:text-[#133A69] pointer-events-none transition-colors" />
                                </div>
                            </div>

                            {/* Date Range Group */}
                            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">From Date</label>
                                    <input
                                        type="date"
                                        value={filters.fromDate}
                                        onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                        className="w-full px-5 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 outline-none focus:bg-white focus:border-blue-500 transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-700 uppercase tracking-widest px-1">To Date</label>
                                    <input
                                        type="date"
                                        value={filters.toDate}
                                        onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                        className="w-full px-5 py-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-900 outline-none focus:bg-white focus:border-blue-500 transition-all"
                                    />
                                </div>
                            </div>

                            {/* Clear Action */}
                            <div className="flex items-end">
                                <button
                                    onClick={clearFilters}
                                    className="w-full py-3 bg-gray-900 hover:bg-gray-800 text-white border border-transparent rounded-xl text-xs font-bold uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <FiRepeat size={14} />
                                    Reset Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Leave Records Table & Mobile View */}
                <LeaveRecordTable
                    filteredRecords={filteredRecords}
                    expandedRows={expandedRows}
                    toggleRow={toggleRow}
                    getStatusStyle={getStatusStyle}
                    calculateItemDeduction={calculateItemDeduction}
                    processedLeaves={processedLeaves}
                />
            </div>
        </>
    );
};

export default EmployeeHistory;
